import React, { useCallback, useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import cx from 'classnames';

import { CONTENT_CARD, CONTENT_CARD_TEMPLATE, CONTENT_EMPTY_MESSAGE, CONTENT_FILTER, CONTENT_LOADING_MESSAGE, CONTENT_PAGINATION_BUTTON } from "../../../constants/widgetTypes";

import { Widget, WidgetsContext } from "../core";
import { AutoGrowLayout } from "../autoGrowLayout";
import { useWidgetChildrenSelector } from "../core/hooks";

import GridView from "./layouts/grid";
import InfiniteScroll from "../../InfiniteScroll";
import Content from "../core/content";

import styles from './content.module.scss';
import BackgroundView from "../background";
import { getBoxShadow } from "../../../services/cssHelper.service";
import { FilterWidget } from "./filters";

const ContentWidgetComponent = ({ className, style, widgetProps, breakpoint, onClick }) => {
  const context = React.useContext(WidgetsContext);
  const contentRef = React.useRef(null);
  const storyboardType = _.get(context, 'storyboard.type');

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const stretched = _.get(widgetProps, 'properties.desktop.stretched', false) && storyboardType !== 'INTERACTIVE_CONTENT';
  const showfilters = _.get(widgetProps, 'properties.desktop.filters', false) && storyboardType == 'RESOURCE_CENTER';
  const showSearch = _.get(widgetProps, 'properties.desktop.search', false) && storyboardType == 'RESOURCE_CENTER';
  const background = _.get(widgetProps, 'properties.desktop.background', null);

  const childrenWidgets = useWidgetChildrenSelector(id);
  const contentFilters = childrenWidgets.find(item => _.get(item, 'type') === CONTENT_FILTER);
  const pagination = _.get(widgetProps, 'properties.desktop.layoutStyle.pagination', 'infinite_scroll');
  const paginationButton = pagination === 'load_more'
    && childrenWidgets.find(item => _.get(item, 'type') === CONTENT_PAGINATION_BUTTON);

  var widgetStyle = {
    ...style,
    ...getStyles(context.storyboard, widgetProps, breakpoint)
  }

  return (
    <AutoGrowLayout ref={contentRef} editorSession={context.session} breakpoint={breakpoint} widget={widgetProps}>
      <div id={id}
        ref={contentRef}
        className={cx(className, styles.content, 'content_widget', {
          [styles.stretched]: stretched,
          [styles.mobile]: breakpoint === 'mobile'
        })}
        style={widgetStyle}
        data-widget-id={id}
        data-widget-type={type}
      >
        <BackgroundView background={background} breakpoint={breakpoint} />

        {(showfilters || showSearch) && contentFilters &&
          <div className={styles.filterContainer}>
            <Widget widgetProps={contentFilters} breakpoint={breakpoint} />
          </div>
        }

        <ContentLayoutView widget={widgetProps} breakpoint={breakpoint} />
        {paginationButton &&
          <div className={cx(styles.pagination, 'pagination_button_container')}>
            <Widget key={paginationButton.uid} contentGrid={id} widgetProps={paginationButton} breakpoint={breakpoint} />
          </div>
        }
      </div>
    </AutoGrowLayout>
  );
}

const ContentLayoutView = ({ widget, breakpoint }) => {
  const context = React.useContext(WidgetsContext);
  const contentContext = React.useContext(Content.Context);

  const id = _.get(widget, 'uid');

  const layoutContent = _.get(widget, 'content', null);
  const filters = _.get(widget, 'filters', []);
  const fetch = _.get(layoutContent, 'mappings.fetch', false);

  const mappings = _.get(layoutContent, 'mappings.items') || [];
  const loading = _.get(layoutContent, 'mappings.loading', true);
  const fetched = _.get(layoutContent, 'mappings.fetched', false);
  const pagination = _.get(widget, 'properties.desktop.layoutStyle.pagination', 'infinite_scroll');

  const currentPage = _.get(widget, 'content.mappings.pagination.current');
  const totalPages = _.get(widget, 'content.mappings.pagination.totalPages');

  const itemsPerPage = _.get(widget, 'properties.desktop.layoutStyle.itemsPerPage', 2);
  const rowSpacing = _.get(widget, 'properties.desktop.layoutStyle.rowSpacing', 10);
  const columnSpacing = _.get(widget, 'properties.desktop.layoutStyle.columnSpacing', 10);
  const alignment = _.get(widget, 'properties.desktop.layoutStyle.alignment') || 'center';

  const cardTemplate = useSelector(state => {
    const widgets = _.get(state, `${context.selector}.widgets`) || [];
    const card = widgets.find(item => item.parent === id && item.type === CONTENT_CARD_TEMPLATE);
    return card;
  });

  const width = _.get(cardTemplate, `properties.${breakpoint}.layout.width`);

  var widgetChildren = useWidgetChildrenSelector(id) || [];

  const loadingWidget = widgetChildren.find(item => item.parent === id && item.type === CONTENT_LOADING_MESSAGE);
  const emptyMessage = widgetChildren.find(item => item.parent === id && item.type === CONTENT_EMPTY_MESSAGE);

  widgetChildren = widgetChildren.filter(item => {
    return _.get(item, `type`) === CONTENT_CARD;
  });

  widgetChildren = _.orderBy(widgetChildren, 'order', 'asc');

  useEffect(() => {
    if (id || fetch) {
      contentContext.fetchMappingsForContentGrid(id, 0, itemsPerPage);
    }
  }, [id, fetch]);

  const handleFetchMappings = () => {
    const nextPage = currentPage + 1;
    if (nextPage >= totalPages) {
      return;
    }

    contentContext.fetchMappingsForContentGrid(id, nextPage, itemsPerPage);
  }

  const wrapWithInfiniteScroll = (children) => {
    if (pagination === 'infinite_scroll') {
      return (
        <InfiniteScroll loading={loading} loadData={handleFetchMappings}>
          {children}
        </InfiniteScroll>
      );
    }
    return children;
  }


  return (
    <div className={styles.layoutRender}>
      {!loading && fetched && _.size(mappings) === 0 && emptyMessage &&
        <Widget widgetProps={emptyMessage} breakpoint={breakpoint} />
      }

      {loading && _.size(mappings) === 0 && loadingWidget &&
        <Widget widgetProps={loadingWidget} breakpoint={breakpoint} />
      }

      {wrapWithInfiniteScroll(
        <GridView className={loading && loadingWidget ? styles.dimLoader : null} width={width} rowSpacing={rowSpacing} columnSpacing={columnSpacing} alignment={alignment}>
          {loading && _.size(mappings) === 0 &&
            _.times(itemsPerPage).map(index => {
              return <Widget key={index} widgetProps={cardTemplate} breakpoint={breakpoint} />
            })
          }

          {widgetChildren.map((item, index) => {
            return <Widget key={item.uid} widgetProps={item} breakpoint={breakpoint} />
          })}

          {loading && _.size(mappings) > 0 &&
            _.times(1).map(index => {
              return <Widget key={index} widgetProps={cardTemplate} breakpoint={breakpoint} />
            })
          }
        </GridView>
      )}
    </div>
  );
}

function getStyles(storyboard, widgetProps, breakpoint) {
  const storyboardType = _.get(storyboard, 'type');

  const stretched = _.get(widgetProps, 'properties.desktop.stretched', false) && storyboardType !== 'INTERACTIVE_CONTENT';
  const margin = _.get(widgetProps, 'properties.desktop.margin', 0);

  var widgetStyles = {

  }

  if (breakpoint === 'desktop') {
    widgetStyles = {
      ...widgetStyles,
      width: `1200px`,
    }

    if (stretched) {
      widgetStyles = {
        ...widgetStyles,
        marginLeft: margin,
        width: `calc(100% - 2 * ${margin}px)`,
        left: `calc((100% - 2 * ${margin}px))/2)`
      }
    }
  }
  //DropShadow
  const shadow = _.get(widgetProps, `properties.desktop.shadow`, {});
  const shadowStyle = getBoxShadow(shadow);
  widgetStyles = {
    ...widgetStyles,
    ...shadowStyle
  }

  return widgetStyles;
}

const contentWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'breakpoint', 'children'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

export const ContentWidget = React.memo(ContentWidgetComponent, contentWidgetEquals);

